import { z } from 'zod';
import { hasPermission, type Role } from '../constants/rbac';

/**
 * Настройки безопасности — общие для всех, одна запись в `app_setting`
 * под ключом `security`.
 *
 * Пока здесь одно правило: сколько строк уходит в буфер обмена за одно
 * нажатие. Заказчик попросил ограничить копирование всем, кроме
 * администратора, — раньше выделенное уходило целиком, хоть вся таблица.
 *
 * Чего это ограничение НЕ делает: копирование происходит в браузере, и
 * сервер узнаёт о нём только постфактум, из журнала (Р-10). Значит предел —
 * это преграда от небрежности и повод остановиться, а не защита от того,
 * кто откроет консоль разработчика. Наглухо закрыть вынос данных можно
 * только убрав копирование у роли целиком, а тогда рекрутёр не сможет
 * перенести телефон кандидата в мессенджер.
 */

export const COPY_ROW_LIMIT_MIN = 1;
export const COPY_ROW_LIMIT_MAX = 1000;
/** Значение, с которым правило появилось: «не больше пяти строк за раз». */
export const DEFAULT_COPY_ROW_LIMIT = 5;

/**
 * Предел строк. `null` — без ограничения.
 *
 * Именно `null`, а не ноль и не отдельный признак «включено»: два поля
 * рано или поздно разойдутся, и в базе окажется «выключено, но 5».
 */
export const copyRowLimitSchema = z
  .number()
  .int('Число строк — целое')
  .min(COPY_ROW_LIMIT_MIN, `Не меньше ${COPY_ROW_LIMIT_MIN}`)
  .max(COPY_ROW_LIMIT_MAX, `Не больше ${COPY_ROW_LIMIT_MAX}`)
  .nullable();

export const securitySettingsSchema = z.object({
  copyRowLimit: copyRowLimitSchema.default(DEFAULT_COPY_ROW_LIMIT),
});

export type SecuritySettings = z.infer<typeof securitySettingsSchema>;

export const DEFAULT_SECURITY_SETTINGS: SecuritySettings = securitySettingsSchema.parse({});

/**
 * Тело PATCH. Поле обязательно и `.strict()`: «сохранить настройки»
 * должно быть однозначным действием, а не «поменяй, что узнаешь».
 */
export const securitySettingsPatchSchema = z
  .object({ copyRowLimit: copyRowLimitSchema })
  .strict('Здесь принимается только предел строк для копирования');

/**
 * Сколько строк этой роли можно скопировать за раз. `null` — сколько угодно.
 *
 * Одна функция на сервер и на браузер: сервер отдаёт сырую настройку,
 * решение принимается здесь. Считать «действующий предел» ещё и на сервере
 * значило бы завести второй ответ на тот же вопрос — и однажды он
 * разойдётся с первым.
 */
export function copyRowLimitFor(settings: SecuritySettings, role: Role): number | null {
  if (settings.copyRowLimit === null) return null;
  if (hasPermission(role, 'copy.unlimited')) return null;
  return settings.copyRowLimit;
}
